import { Camera, X } from 'lucide-react'
import { useRef } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'

type PhotoInputProps = {
  /** 現在選択中のファイル一覧 */
  files: File[]
  /** プレビュー URL 一覧 (data URL) */
  previews: string[]
  /** ファイル/プレビュー変更時のコールバック */
  onFilesChange: (files: File[], previews: string[]) => void
  /** 最大アップロード数 (default: 5) */
  maxFiles?: number
  /** ヘッダーラベル (省略可。指定するとラベル+ボタンの行を描画) */
  label?: string
}

/**
 * 写真入力コンポーネント
 *
 * Camera ボタン + hidden file input + プレビューグリッドを一括で提供する。
 * 食事追加/編集/テンプレート登録など複数画面で共通利用する。
 */
export function PhotoInput({ files, previews, onFilesChange, maxFiles = 5, label }: PhotoInputProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files
    if (!selected) return
    const newFiles = Array.from(selected).slice(0, maxFiles - files.length)
    if (newFiles.length === 0) {
      toast.error(`最大${maxFiles}枚までです`)
      return
    }

    const updatedFiles = [...files, ...newFiles]
    let currentPreviews = [...previews]

    for (const file of newFiles) {
      const reader = new FileReader()
      reader.onload = () => {
        currentPreviews = [...currentPreviews, reader.result as string]
        onFilesChange(updatedFiles, currentPreviews)
      }
      reader.readAsDataURL(file)
    }

    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const removeImage = (index: number) => {
    onFilesChange(
      files.filter((_, i) => i !== index),
      previews.filter((_, i) => i !== index),
    )
  }

  const trigger = (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-7"
        onClick={() => fileInputRef.current?.click()}
        disabled={files.length >= maxFiles}
      >
        <Camera className="size-4" />
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple={maxFiles > 1}
        className="hidden"
        onChange={handleImageSelect}
      />
    </>
  )

  return (
    <div className="space-y-2">
      {label && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground flex-1">{label}</span>
          {trigger}
        </div>
      )}
      {!label && <div className="flex items-center justify-end">{trigger}</div>}
      {previews.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {previews.map((src, i) => (
            <div key={i} className="relative">
              <img src={src} alt={`photo-${i + 1}`} className="h-20 w-20 rounded-md object-cover" />
              <button
                type="button"
                onClick={() => removeImage(i)}
                className="absolute -top-1.5 -right-1.5 rounded-full bg-destructive p-0.5! text-destructive-foreground"
              >
                <X className="size-3" />
              </button>
            </div>
          ))}
          {files.length < maxFiles && (
            <span className="text-[10px] text-muted-foreground self-end">
              {files.length}/{maxFiles}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
